import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { Link as RouterLink } from "react-router-dom";
import { FaDownload, FaSun, FaMoon, FaTerminal, FaBuilding } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";

const RESUME_URL =
  "https://drive.google.com/file/d/1votzGnOmgdfOcV-FRgTl68EQ8YamNWCx/view?usp=share_link";

const Navbar = ({ onOpenCmd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("hero");
  const [scrolled, setScrolled] = useState(false);
  const { isDark, toggle } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = (link) => {
    setActiveLink(link);
    setIsOpen(false);
  };

  const navItems = [
    { id: "hero", label: "home" },
    { id: "about", label: "about" },
    { id: "portfolio", label: "projects" },
    { id: "skills", label: "skills" },
    { id: "contact", label: "contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-dark-950/90 backdrop-blur-md border-b border-sky-400/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4">
        <Link
          to="hero"
          smooth={true}
          offset={-70}
          duration={500}
          className="cursor-pointer"
          onClick={() => handleLinkClick("hero")}
        >
          <span className="font-mono text-base sm:text-lg font-semibold">
            <span className="text-sky-400">{"<"}</span>
            <span className="text-white">Petrus</span>
            <span className="text-sky-400">{"/>"}</span>
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.id}
              smooth={true}
              offset={-70}
              duration={500}
              className={`px-3 lg:px-4 py-2 font-mono text-xs cursor-pointer transition-all duration-300 ${
                activeLink === item.id
                  ? "text-sky-400 bg-sky-400/8 border border-sky-400/25"
                  : "text-gray-400 hover:text-sky-300 border border-transparent"
              }`}
              onClick={() => handleLinkClick(item.id)}
            >
              .{item.label}()
            </Link>
          ))}
          <RouterLink
            to="/building"
            className="px-3 lg:px-4 py-2 font-mono text-xs text-gray-400 hover:text-sky-300 border border-transparent hover:border-sky-400/20 transition-all duration-300 flex items-center gap-1.5"
          >
            <FaBuilding className="text-[10px]" />
            3d
          </RouterLink>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-tech ml-2 text-xs"
          >
            <FaDownload className="text-[10px]" />
            resume
          </a>

          {/* Theme toggle */}
          <button
            onClick={toggle}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="ml-1 p-2 text-gray-400 hover:text-sky-400 transition-colors duration-300"
          >
            {isDark ? <FaSun className="text-sm" /> : <FaMoon className="text-sm" />}
          </button>

          {/* Command palette trigger */}
          <button
            onClick={onOpenCmd}
            title="Open command palette (Ctrl+K)"
            className="p-2 text-gray-400 hover:text-sky-400 transition-colors duration-300"
          >
            <FaTerminal className="text-sm" />
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-gray-300 hover:text-sky-400 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-5 h-4 flex flex-col justify-between">
            <span className={`block h-px bg-current transition-all duration-300 ${isOpen ? "rotate-45 translate-y-1.5" : ""}`} />
            <span className={`block h-px bg-current transition-all duration-300 ${isOpen ? "opacity-0" : ""}`} />
            <span className={`block h-px bg-current transition-all duration-300 ${isOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-400 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-dark-950/95 backdrop-blur-md border-t border-sky-400/10 px-4 py-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.id}
              smooth={true}
              offset={-70}
              duration={500}
              className={`block px-4 py-2.5 font-mono text-xs transition-all duration-300 ${
                activeLink === item.id
                  ? "text-sky-400 bg-sky-400/8 border-l-2 border-sky-400"
                  : "text-gray-400 hover:text-sky-300 border-l-2 border-transparent"
              }`}
              onClick={() => handleLinkClick(item.id)}
            >
              .{item.label}()
            </Link>
          ))}
          <RouterLink
            to="/building"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs text-gray-400 hover:text-sky-300 border-l-2 border-transparent transition-colors"
          >
            <FaBuilding className="text-[10px]" />
            3d_building
          </RouterLink>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs text-sky-400 hover:text-sky-300 transition-colors"
          >
            <FaDownload className="text-[10px]" />
            download_resume
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
